import { BASE_PUBLIC_RPC_URL, BASE_SEPOLIA_HTTP_RPC_URL } from "@/config";
import { JsonRpcProvider, FetchRequest } from "ethers";

const RPC_TIMEOUT_MS = 10_000; // 10 seconds per RPC call

const CAPACITY_LIMIT_ERRORS = [
  "monthly capacity limit exceeded",
  "rate limit",
  "too many requests",
  "429",
  "capacity",
  "exceeded",
];

function isCapacityLimitError(error: unknown): boolean {
  const errorMessage =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  return CAPACITY_LIMIT_ERRORS.some((keyword) => errorMessage.includes(keyword));
}

function createProvider(url: string): JsonRpcProvider {
  const fetchReq = new FetchRequest(url);
  fetchReq.timeout = RPC_TIMEOUT_MS;
  return new JsonRpcProvider(fetchReq);
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`RPC call timed out after ${ms}ms`)), ms)
    ),
  ]);
}

export async function withFallbackProvider<T>(
  operation: (provider: JsonRpcProvider) => Promise<T>
): Promise<T> {
  const primaryProvider = createProvider(BASE_SEPOLIA_HTTP_RPC_URL);

  try {
    return await withTimeout(operation(primaryProvider), RPC_TIMEOUT_MS);
  } catch (error) {
    if (isCapacityLimitError(error)) {
      console.warn(
        "Primary RPC capacity limit exceeded, falling back to public RPC:",
        BASE_PUBLIC_RPC_URL
      );
      const fallbackProvider = createProvider(BASE_PUBLIC_RPC_URL);
      return await withTimeout(operation(fallbackProvider), RPC_TIMEOUT_MS);
    }
    throw error;
  }
}

export function createProviderWithFallback(): {
  primary: JsonRpcProvider;
  fallback: JsonRpcProvider;
  executeWithFallback: <T>(
    operation: (provider: JsonRpcProvider) => Promise<T>
  ) => Promise<T>;
} {
  const primary = createProvider(BASE_SEPOLIA_HTTP_RPC_URL);
  const fallback = createProvider(BASE_PUBLIC_RPC_URL);

  return {
    primary,
    fallback,
    executeWithFallback: async <T>(
      operation: (provider: JsonRpcProvider) => Promise<T>
    ): Promise<T> => {
      try {
        return await withTimeout(operation(primary), RPC_TIMEOUT_MS);
      } catch (error) {
        if (isCapacityLimitError(error)) {
          console.warn(
            "Primary RPC capacity limit exceeded, falling back to public RPC"
          );
          return await withTimeout(operation(fallback), RPC_TIMEOUT_MS);
        }
        throw error;
      }
    },
  };
}
